/**
 * Progress Details Core
 *
 * Core clinician progress details view that works in both standalone (routed page)
 * and embedded (inline within leadership table) modes.
 *
 * Extracted from ProgressDetailsPage to enable Phase 7 leadership inline expansion
 * without duplicating the entire view.
 *
 * - embedded={false}: Full page with h1 title, card-wrapped error/loading/empty states
 * - embedded={true}: No page title, compact inline error/loading/empty states
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, RefreshCcw, BarChart3 } from 'lucide-react'
import { ProviderProfileBar } from './components/ProviderProfileBar'
import { MeasureAccordion } from './components/MeasureAccordion'
import { PatientGrid } from './components/PatientGrid'
import PIMsGrid from './components/PIMsGrid'
import AuditGrid from './components/AuditGrid'
import { EDSendoutGrid } from './components/EDSendoutGrid'
import FullPatientHeatmap from './components/FullPatientHeatmap'
import { PatientModal } from '../modals/PatientModal'
import { AuditModal } from '../modals/AuditModal'
import { AskProviderQuestionModal } from './components/AskProviderQuestionModal'
import { ExportReportModal } from './components/ExportReportModal'
import { buildDetailResetKey, isCurrentDetailRequest, useDetailMode } from './hooks/useDetailMode'
import { equipApi } from '@/services/equipApi'
import { equipPerformanceKeys } from '@/hooks/equip/useEquipPerformance'
import { useMyContext } from '@/hooks/equip/useEquipPerformance'
import { getActiveMeasures } from '@/lib/equip-utils'
import { useCurrentQuarter } from '@/hooks/equip/useCurrentQuarter'
import { useMeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'
import type {
  ProviderPerformanceDetail,
  PatientDrilldownItem,
  EDSendoutEncounterRow,
} from '@/lib/equip-types'

interface ProgressDetailsCoreProps {
  providerEmail?: string | null
  /** When true, strips page title and uses compact error/loading/empty states */
  embedded?: boolean
  /** Phase 7: Initial year for quarter sync when embedded in leadership expansion */
  initialYear?: number
  /** Phase 7: Initial quarter for quarter sync when embedded in leadership expansion */
  initialQuarter?: number
  /** Deep link: auto-open PatientModal for this patient ID on mount */
  initialPatientId?: string | null
}

export function ProgressDetailsCore({
  providerEmail,
  embedded = false,
  initialYear,
  initialQuarter,
  initialPatientId,
}: ProgressDetailsCoreProps) {
  // Current quarter from backend API (cached, gated by EquipScopeContext loading)
  const currentQ = useCurrentQuarter()

  // Quarter selection state — remains local UI state (not server state)
  const [selectedQuarter, setSelectedQuarter] = useState(
    initialYear && initialQuarter
      ? { year: initialYear, quarter: initialQuarter }
      : { year: currentQ.year, quarter: currentQ.quarter }
  )

  // Sync quarter to shared context (for snapshot banner)
  const scope = useEquipScopeOptional()
  useEffect(() => {
    scope?.setQuarter(selectedQuarter.year, selectedQuarter.quarter)
  }, [selectedQuarter.year, selectedQuarter.quarter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Drill-down state (patient list from measure bar click) — remains local/imperative
  const [patients, setPatients] = useState<PatientDrilldownItem[]>([])
  const [edSendoutEncounters, setEdSendoutEncounters] = useState<EDSendoutEncounterRow[]>([])
  const [isDrilldownLoading, setIsDrilldownLoading] = useState(false)
  const [drilldownError, setDrilldownError] = useState<string | null>(null)

  // Patient modal state — seeded from deep link param when provided
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(initialPatientId ?? null)
  const [selectedPatientTab, setSelectedPatientTab] = useState<'measures' | 'pims'>('measures')

  // Audit modal state
  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null)
  const [selectedAuditType, setSelectedAuditType] = useState<string>('coding')

  // Ask a Question modal state
  const [askProviderQuestionOpen, setAskProviderQuestionOpen] = useState(false)

  // Export Report modal state
  const [showExportModal, setShowExportModal] = useState(false)

  // Profile bar expand state — lifted here so ProgressDetailsCore observes expand changes.
  // setIsProfileExpanded is passed as onExpandChange; the bar owns the toggle itself.
  const [_isProfileExpanded, setIsProfileExpanded] = useState(false)

  // DetailMode state machine. Include every scope dimension available to this
  // embedded/full view so a provider or role change cannot retain stale detail.
  const detailScopeKey = [
    scope?.isAdmin ? 'admin' : 'user',
    scope?.isSeniorLead ? 'senior' : 'non-senior',
    scope?.showClinicianDropdown ? 'clinician-selector' : 'self-only',
    scope?.selectedManager ?? '',
    scope?.selectedSeniorLead ?? '',
    scope?.selectedClinician ?? '',
    scope?.selectedPhysicianNpi ?? '',
  ].join(':')
  const detailResetKey = buildDetailResetKey(
    detailScopeKey,
    providerEmail,
    selectedQuarter.year,
    selectedQuarter.quarter,
  )
  const drilldownRequestIdRef = useRef(0)
  const {
    detailMode,
    handleColumnClick,
    handleTotalPatientsClick,
    handleAcoPatientsClick,
    toggleAcoFilter,
    toggleViewMode,
  } = useDetailMode({ resetKey: detailResetKey })

  // Clear detail payloads and invalidate in-flight drilldown responses when
  // provider, role, or quarter scope changes.
  useEffect(() => {
    drilldownRequestIdRef.current += 1
    setPatients([])
    setEdSendoutEncounters([])
    setDrilldownError(null)
    setIsDrilldownLoading(false)
  }, [detailResetKey])

  // ---------------------------------------------------------------------------
  // React Query: Performance data
  //
  // Single conditional useQuery — avoids calling both hooks when only one path
  // is active. The query key switches between "me" and "provider" based on whether
  // providerEmail is set, so React Query cache is keyed per-path correctly.
  // isLoading: true only on FIRST fetch (no cached data). False on background refetch.
  // ---------------------------------------------------------------------------
  const perfQuery = useQuery<ProviderPerformanceDetail>({
    queryKey: providerEmail
      ? equipPerformanceKeys.providerPerformance(providerEmail, selectedQuarter.year, selectedQuarter.quarter)
      : equipPerformanceKeys.myPerformance(selectedQuarter.year, selectedQuarter.quarter),
    queryFn: () =>
      providerEmail
        ? equipApi.performance.getProviderPerformance(providerEmail, {
            year: selectedQuarter.year,
            quarter: selectedQuarter.quarter,
          })
        : equipApi.performance.getMyPerformance({
            year: selectedQuarter.year,
            quarter: selectedQuarter.quarter,
          }),
  })

  // Context — only needed in self-view (providerEmail is null).
  // Always called per React hooks rules; result only used when !providerEmail.
  // The /me/context endpoint is fast and cacheable — minimal cost of always calling.
  const contextQuery = useMyContext()

  // Derive combined state from query results
  const performance = perfQuery.data ?? null
  const userContext = providerEmail ? null : (contextQuery.data ?? null)
  const isLoading = perfQuery.isLoading || (!providerEmail && contextQuery.isLoading)
  const rawError = perfQuery.error ?? contextQuery.error ?? null
  const isForbidden = (rawError as { status?: number } | null)?.status === 403
  const error = rawError
    ? isForbidden
      ? "You don't have permission to view this clinician's data."
      : ((rawError as Error).message ?? 'Something went wrong loading this view.')
    : null

  const isCurrentQuarter =
    selectedQuarter.year === currentQ.year && selectedQuarter.quarter === currentQ.quarter

  // Handle quarter change from slicer
  const handleQuarterChange = (year: number, quarter: number) => {
    setSelectedQuarter({ year, quarter })
    // When selectedQuarter changes, query key changes → React Query fetches fresh data.
    // DetailMode hook will auto-reset via resetKey dependency.
    setPatients([])
    setEdSendoutEncounters([])
    setDrilldownError(null)
  }

  // Handle chart bar click - delegates to useDetailMode and fetches drill-down data.
  // Drilldown is user-triggered (not mount-triggered) — stays as an imperative call.
  const handleChartColumnClick = async (measureName: string, measureType: string) => {
    const requestId = ++drilldownRequestIdRef.current
    const requestScopeKey = detailResetKey
    setDrilldownError(null)
    await handleColumnClick(measureName, measureType)

    if (!isCurrentDetailRequest(requestId, drilldownRequestIdRef.current, requestScopeKey, detailResetKey)) {
      return
    }

    // Skip API drill-down for zero-denominator measures (no patients to fetch)
    const clickedMeasure = activeMeasures.find((m) => m.measureName === measureName)
    if (clickedMeasure?.isZeroDenominator) {
      return
    }

    // Fetch drill-down data for quality measures and ACO_Visits
    if ((measureType === 'quality' || measureType === 'aco_visits') && performance?.npi) {
      setIsDrilldownLoading(true)
      try {
        const drilldownData = await equipApi.patients.getDrilldown(
          String(performance.npi),
          measureName,
          {
            year: selectedQuarter.year,
            quarter: selectedQuarter.quarter,
          }
        )
        if (isCurrentDetailRequest(requestId, drilldownRequestIdRef.current, requestScopeKey, detailResetKey)) {
          setPatients(drilldownData)
        }
      } catch (err) {
        console.error('Failed to fetch drill-down data:', err)
        if (isCurrentDetailRequest(requestId, drilldownRequestIdRef.current, requestScopeKey, detailResetKey)) {
          setDrilldownError(err instanceof Error ? err.message : 'Failed to load patient data')
        }
      } finally {
        if (requestId === drilldownRequestIdRef.current) setIsDrilldownLoading(false)
      }
    } else if (measureType === 'ed_sendout' && performance?.npi) {
      setIsDrilldownLoading(true)
      try {
        const data = await equipApi.dashboard.getEDSendoutDrilldown(String(performance.npi), {
          year: selectedQuarter.year,
          quarter: selectedQuarter.quarter,
        })
        if (isCurrentDetailRequest(requestId, drilldownRequestIdRef.current, requestScopeKey, detailResetKey)) {
          setEdSendoutEncounters(data)
        }
      } catch (err) {
        console.error('Failed to fetch ED sendout data:', err)
        if (isCurrentDetailRequest(requestId, drilldownRequestIdRef.current, requestScopeKey, detailResetKey)) {
          setDrilldownError(err instanceof Error ? err.message : 'Failed to load ED sendout data')
        }
      } finally {
        if (requestId === drilldownRequestIdRef.current) setIsDrilldownLoading(false)
      }
    }
  }

  // Handle patient click in grid
  const handlePatientClick = (patient: PatientDrilldownItem) => {
    setSelectedPatientId(String(patient.patientId))
    setSelectedPatientTab('measures')
  }

  // Handle PIMs patient click (different signature for PIMsGrid)
  const handlePimsPatientClick = (patient: { patientId: number; defaultTab: string }) => {
    setSelectedPatientId(String(patient.patientId))
    setSelectedPatientTab(patient.defaultTab as 'measures' | 'pims')
  }

  // Handle audit click
  const handleAuditClick = (auditId: number, auditType: string) => {
    setSelectedAuditId(auditId)
    setSelectedAuditType(auditType)
  }

  // Handle modal close
  const handleModalClose = () => {
    setSelectedPatientId(null)
  }

  // Handle audit modal close
  const handleAuditModalClose = () => {
    setSelectedAuditId(null)
  }

  // Resolve thresholds from the canonical config for this quarter.
  const thresholdMap = useMeasureThresholdMap(selectedQuarter.year, selectedQuarter.quarter)

  // Individual provider view always shows filtered measures (not leader aggregation)
  const activeMeasures = useMemo(
    () => (performance ? getActiveMeasures(performance, thresholdMap, false) : []),
    [performance, thresholdMap]
  )

  // Error state
  if (error) {
    if (embedded) {
      return (
        <div className="flex items-center gap-2 py-4 px-2">
          <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm text-muted-foreground">
            {isCurrentQuarter
              ? error
              : `No data available for Q${selectedQuarter.quarter} ${selectedQuarter.year}.`}
          </p>
          {isCurrentQuarter ? (
            <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={() => perfQuery.refetch()}>
              <RefreshCcw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 text-primary"
              onClick={() => handleQuarterChange(currentQ.year, currentQ.quarter)}
            >
              View current quarter
            </Button>
          )}
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Progress Details</h1>
        <Card className={isCurrentQuarter ? 'border-destructive/50' : undefined}>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${isCurrentQuarter ? 'bg-destructive/10' : 'bg-muted'}`}>
              {isCurrentQuarter
                ? <AlertCircle className="h-6 w-6 text-destructive" />
                : <BarChart3 className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="text-center">
              {isCurrentQuarter ? (
                <>
                  <p className="text-lg font-semibold text-destructive">Unable to Load Performance Data</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">{error}</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold">No Data for Q{selectedQuarter.quarter} {selectedQuarter.year}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Historical data will be available here once the quarter is complete.
                  </p>
                </>
              )}
            </div>
            {isCurrentQuarter ? (
              <Button variant="outline" onClick={() => perfQuery.refetch()}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            ) : (
              <Button onClick={() => handleQuarterChange(currentQ.year, currentQ.quarter)}>
                View Current Quarter
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // Loading state — isLoading is true only on FIRST fetch (no cached data).
  // On subsequent mounts within staleTime window, isLoading is false (cache hit).
  if (isLoading) {
    if (embedded) {
      return (
        <div className="space-y-3 py-2">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-3 w-36 mb-1.5" />
              <Skeleton className="h-2.5 w-52" />
            </div>
          </div>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full rounded" />
            ))}
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        {/* ProviderProfileBar skeleton */}
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-4 w-48 mb-2" />
              <Skeleton className="h-3 w-64" />
            </div>
            <Skeleton className="h-9 w-24" />
          </div>
        </Card>
        {/* HorizontalPerformanceChart skeleton - 4-5 bars */}
        <Card className="p-6">
          <Skeleton className="h-4 w-32 mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 flex-1" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    )
  }

  // Empty state for no performance data
  if (!performance && !error) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-6 text-center">
          <div>
            <BarChart3 className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm font-medium">No Performance Data Available</p>
            <p className="text-xs text-muted-foreground mt-0.5">Data not yet calculated for this quarter.</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Progress Details</h1>
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-lg font-semibold">No Performance Data Available</p>
              <p className="text-sm text-muted-foreground mt-1">
                Performance data for this quarter has not been calculated yet.
              </p>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Page Header — standalone only */}
      {!embedded && (
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl font-bold">Progress Details</h1>
          </div>
          {/* Quarter selection in ProviderProfileBar below */}
        </div>
      )}

      {/* Provider Profile Bar - renders in BOTH standalone and embedded modes. */}
      <div>
        <ProviderProfileBar
          performance={performance}
          userContext={userContext}
          selectedQuarter={selectedQuarter}
          onQuarterChange={handleQuarterChange}
          onTotalPatientsClick={handleTotalPatientsClick}
          onAcoPatientsClick={handleAcoPatientsClick}
          onAskQuestion={() => setAskProviderQuestionOpen(true)}
          onDownloadReport={() => setShowExportModal(true)}
          onExpandChange={(expanded) => {
            setIsProfileExpanded(expanded)
            if (expanded && detailMode.type !== 'all-patients') {
              handleTotalPatientsClick()
            }
          }}
          expandedContent={
            performance ? (
              <FullPatientHeatmap
                performance={performance}
                providerNpi={String(performance.npi)}
                year={selectedQuarter.year}
                quarter={selectedQuarter.quarter}
                isLoading={false}
                onPatientClick={handlePatientClick}
                providerName={performance.providerName}
                providerEmail={performance.providerEmail}
                acoFilter={detailMode.type === 'all-patients' ? detailMode.acoFilter : 'all'}
                viewMode={detailMode.type === 'all-patients' ? detailMode.viewMode : 'patient'}
                onToggleAcoFilter={toggleAcoFilter}
                onToggleViewMode={toggleViewMode}
                isLeader={false}
              />
            ) : null
          }
        />
      </div>

      {/* Measure Accordion — bars + inline drill-down */}
      <div>
      <MeasureAccordion
        measures={activeMeasures}
        detailMode={detailMode}
        isDrilldownLoading={isDrilldownLoading}
        drilldownError={drilldownError}
        onDismissError={() => setDrilldownError(null)}
        onMeasureClick={handleChartColumnClick}
        renderGrid={(_measure, dm) => {
          // Zero-denominator measures have no patients — skip grid rendering
          if (_measure.isZeroDenominator) return null
          return (
          <>
            {dm.type === 'quality-measure' && (
              <PatientGrid
                patients={patients}
                measureName={dm.measureName}
                isLoading={isDrilldownLoading}
                onPatientClick={handlePatientClick}
                selectedPatientIds={[]}
                onSelectionChange={() => {}}
                providerName={performance?.providerName}
                providerEmail={performance?.providerEmail}
                year={selectedQuarter.year}
                quarter={selectedQuarter.quarter}
                embedded
              />
            )}
            {dm.type === 'pims' && performance && (
              <PIMsGrid
                providerNpi={String(performance.npi)}
                year={selectedQuarter.year}
                quarter={selectedQuarter.quarter}
                isLoading={false}
                onPatientClick={handlePimsPatientClick}
                embedded
              />
            )}
            {dm.type === 'audit' && performance && (
              <AuditGrid
                providerEmail={performance.providerEmail}
                auditType={dm.auditType}
                year={selectedQuarter.year}
                quarter={selectedQuarter.quarter}
                isLoading={false}
                onAuditClick={handleAuditClick}
                providerName={performance.providerName}
                embedded
              />
            )}
            {dm.type === 'ed-sendout' && (
              <EDSendoutGrid
                encounters={edSendoutEncounters}
                isLoading={isDrilldownLoading}
              />
            )}
          </>
        )}}
      />
      </div>

      {/* Patient Modal */}
      <PatientModal
        patientId={selectedPatientId}
        defaultTab={selectedPatientTab}
        onClose={handleModalClose}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
        providerName={performance?.providerName}
        providerEmail={performance?.providerEmail}
      />

      {/* Audit Modal */}
      <AuditModal
        auditId={selectedAuditId}
        auditType={selectedAuditType}
        onClose={handleAuditModalClose}
        providerName={performance?.providerName}
        providerEmail={performance?.providerEmail}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
      />

      {/* Export Report Modal */}
      {performance && (
        <ExportReportModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          providerEmail={performance.providerEmail}
          providerName={performance.providerName}
          year={selectedQuarter.year}
          quarter={selectedQuarter.quarter}
        />
      )}

      {/* Ask a Question Modal — provider-level, rendered outside other dialogs */}
      {performance && (
        <AskProviderQuestionModal
          open={askProviderQuestionOpen}
          onClose={() => setAskProviderQuestionOpen(false)}
          providerName={performance.providerName}
          providerEmail={performance.providerEmail}
          specialty={performance.bonusEligibleType}
          year={selectedQuarter.year}
          quarter={selectedQuarter.quarter}
        />
      )}
    </div>
  )
}
